/**
 * ScheduleAppointmentDialog — Dialog wrapper around AppointmentForm.
 *
 * Used for the edit-from-event-tap flow (and any other surface that wants the
 * legacy modal entry point). Create-mode now goes through AppointmentDrawer
 * instead so the user picks Add vs Request first.
 *
 * Modes:
 *   - Create: open with no `eventToEdit` — saves via calendarService.addEvent.
 *   - Edit:   open with an `eventToEdit` — saves via calendarService.updateEvent.
 *
 * Special states:
 *   - Verification: when an auto-created (email-extracted) event is opened
 *     for edit and still flagged needsVerification, the form shows a banner
 *     and flips both `verified: true` / `needsVerification: false` on save.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
} from '@mui/material';
import AppointmentForm from '../Calendar/AppointmentForm';
import type { CalendarEvent } from '../../types/calendar';

interface ScheduleAppointmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  eventToEdit?: Partial<CalendarEvent> | null;
  initialDate?: Date | null;
}

const ScheduleAppointmentDialog: React.FC<ScheduleAppointmentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  eventToEdit,
  initialDate,
}) => {
  const isEditMode = !!eventToEdit?.id;
  const dialogTitle = isEditMode ? 'Edit Appointment' : 'Schedule Appointment';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
          {dialogTitle}
        </Typography>
      </DialogTitle>

      {/* pt: 4 (32px) absolutely guarantees the floating Select label on
          "Pet" clears the title boundary even when it shrinks. */}
      <DialogContent sx={{ pt: 4, pb: 2, overflowY: 'auto' }}>
        {open && (
          <AppointmentForm
            onCancel={onClose}
            onSuccess={onSuccess}
            eventToEdit={eventToEdit}
            initialDate={initialDate}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleAppointmentDialog;
